import {
    useLayoutEffect,
    useRef
} from "react";
import {
    Animated,
    Platform
} from "react-native";
import type {
    AnimatedIconClockConstantType,
    AnimatedIconClockType
} from "./type";
import {
    getAnimatedIconContinuous,
    getAnimatedIconDuration
} from "./stylesheet";
import {
    ANIMATED_ICON_START_PROGRESS,
    ANIMATED_ICON_REST_PROGRESS
} from "./constant";
import {
    registerAnimatedIconLoop
} from "./util";
import {
    getMotionDuration,
    useReducedMotion,
    MOTION_EASINGS
} from "../../helpers/motion";

const useAnimatedIcon = ({
    onAnimationEnd,
    animation,
    durations,
    trigger,
    delay,
    plan
}: AnimatedIconClockConstantType): AnimatedIconClockType => {
    const isReduceMotionEnabled = useReducedMotion();

    const animationRef = useRef<Animated.CompositeAnimation | null>(null);

    const releaseLoopRef = useRef<(() => void) | null>(null);

    const onAnimationEndRef = useRef(onAnimationEnd);

    const progress = useRef(new Animated.Value(ANIMATED_ICON_START_PROGRESS)).current;

    const isLoop = trigger === "loop";

    const isAutoPlay = isLoop || trigger === "mount";

    const isActive = plan !== null || animation !== "none";

    const duration = getMotionDuration({
        isReduceMotionEnabled: isReduceMotionEnabled && animation !== "spin",
        duration: getAnimatedIconDuration({
            animation,
            durations,
            plan
        }),
        durations
    });

    const isContinuous = getAnimatedIconContinuous({
        animation,
        plan
    });

    const gap = isActive && isLoop && !isContinuous ? Math.max(durations.loop - duration, 0) : 0;

    const handleStop = () => {
        animationRef.current?.stop();

        animationRef.current = null;

        releaseLoopRef.current?.();

        releaseLoopRef.current = null;
    };

    const handleRun = (fromValue: number) => {
        handleStop();

        if(!isActive) {
            progress.setValue(ANIMATED_ICON_REST_PROGRESS);

            return;
        }

        if(!duration) {
            progress.setValue(ANIMATED_ICON_REST_PROGRESS);

            if(!isLoop) {
                onAnimationEndRef.current?.();
            }

            return;
        }

        const isResumed = fromValue !== ANIMATED_ICON_START_PROGRESS;

        const remainingDuration = isResumed ? duration * (ANIMATED_ICON_REST_PROGRESS - fromValue) / (ANIMATED_ICON_REST_PROGRESS - ANIMATED_ICON_START_PROGRESS) : duration;

        progress.setValue(fromValue);

        const getTiming = (toValue: number, timingDuration: number) => Animated.timing(progress, {
            useNativeDriver: Platform.OS !== "web" && plan === null,
            easing: MOTION_EASINGS.linear,
            duration: timingDuration,
            isInteraction: false,
            toValue
        });

        const timing = getTiming(ANIMATED_ICON_REST_PROGRESS, remainingDuration);

        const cycle = gap ? Animated.sequence([
            timing,
            getTiming(ANIMATED_ICON_REST_PROGRESS, gap)
        ]) : timing;

        const run = isLoop ? Animated.loop(cycle) : cycle;

        const current = delay && !isResumed ? Animated.sequence([
            getTiming(ANIMATED_ICON_START_PROGRESS, delay),
            run
        ]) : run;

        animationRef.current = current;

        if(isLoop) {
            releaseLoopRef.current = registerAnimatedIconLoop();
        }

        current.start(({
            finished
        }) => {
            if(!finished || isLoop) {
                return;
            }

            animationRef.current = null;

            onAnimationEndRef.current?.();
        });
    };

    const handlePlay = () => {
        if(!animationRef.current || isLoop || !isActive) {
            handleRun(ANIMATED_ICON_START_PROGRESS);

            return;
        }

        progress.stopAnimation((value) => {
            handleRun(value);
        });
    };

    useLayoutEffect(() => {
        onAnimationEndRef.current = onAnimationEnd;
    });

    useLayoutEffect(() => {
        if(isAutoPlay) {
            handleRun(ANIMATED_ICON_START_PROGRESS);
        } else {
            handleStop();

            progress.setValue(ANIMATED_ICON_REST_PROGRESS);
        }

        return handleStop;
    }, [
        isAutoPlay,
        animation,
        duration,
        plan,
        isLoop,
        delay,
        gap
    ]);

    return {
        handlePlay,
        progress
    };
};
export default useAnimatedIcon;
